import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Droplets,
  Star,
  Crown,
  Sparkles,
  Zap,
  Users,
  Bluetooth,
  Check,
  Car,
  Clock,
  Smartphone,
} from 'lucide-react';

// XpresWash-aligned pricing plans
const WASH_PLANS = [
  {
    id: 'platinum',
    name: 'Xpress Platinum',
    price: 42,
    singlePrice: 25,
    icon: Crown,
    color: 'jrd-orange',
    badge: 'Best Value',
    features: [
      'Presoak',
      'Soft-touch brushes',
      'Wheel cleaner',
      'Underbody wash',
      'Tri-color foam',
      'Clear coat',
      'Lava shine',
      'Hot wax',
      'Ice tire shine',
      'Free vacuum',
    ],
  },
  {
    id: 'everything',
    name: 'Xpress Everything',
    price: 35,
    singlePrice: 20,
    icon: Sparkles,
    color: 'jrd-green',
    badge: 'Popular',
    features: [
      'Presoak',
      'Soft-touch brushes',
      'Wheel cleaner',
      'Underbody wash',
      'Tri-color foam',
      'Clear coat',
      'Hot wax',
      'Ice tire shine',
      'Free vacuum',
    ],
  },
  {
    id: 'plus-wheels',
    name: 'Xpress Plus Wheels',
    price: 29,
    singlePrice: 15,
    icon: Zap,
    color: 'text-primary',
    badge: null,
    features: [
      'Presoak',
      'Soft-touch brushes',
      'Wheel cleaner',
      'Underbody wash',
      'Tri-color foam',
      'Free vacuum',
    ],
  },
];

// Sample members
const SAMPLE_MEMBERS = [
  { id: 1, name: 'Sarah Johnson', plan: 'Xpress Platinum', vehicle: '2023 Subaru Outback', plate: 'MN ABC-123', rfid: 'XW-00142', since: '2025-08-12', washCount: 47 },
  { id: 2, name: 'Mike Chen', plan: 'Xpress Everything', vehicle: '2024 Tesla Model 3', plate: 'MN DEF-456', rfid: 'XW-00298', since: '2025-10-03', washCount: 31 },
  { id: 3, name: 'Lisa Rodriguez', plan: 'Xpress Plus Wheels', vehicle: '2022 Honda CR-V', plate: 'MN GHI-789', rfid: 'XW-00315', since: '2025-11-18', washCount: 22 },
  { id: 4, name: 'Tom Anderson', plan: 'Xpress Platinum', vehicle: '2023 Ford F-150', plate: 'MN JKL-012', rfid: 'XW-00087', since: '2025-06-01', washCount: 63 },
  { id: 5, name: 'Amy Patel', plan: 'Xpress Everything', vehicle: '2024 Toyota Camry', plate: 'MN MNO-345', rfid: 'XW-00401', since: '2026-01-15', washCount: 8 },
];

type Tab = 'plans' | 'members' | 'kiosk';

export function CarWash() {
  const [activeTab, setActiveTab] = useState<Tab>('plans');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <Droplets className="w-6 h-6 text-jrd-orange" />
          Car Wash
        </h2>
        <p className="text-text-muted text-sm font-mono">
          <span className="text-jrd-orange">//</span> XpresWash unlimited wash management
          <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 bg-jrd-green/10 border border-jrd-green/30 rounded text-jrd-green text-[10px]">
            SAMPLE DATA
          </span>
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-bg-card rounded-lg p-1 border border-text-muted/20 w-fit">
        {([
          { id: 'plans' as Tab, label: 'Wash Plans', icon: Star },
          { id: 'members' as Tab, label: 'Members', icon: Users },
          { id: 'kiosk' as Tab, label: 'Kiosk', icon: Smartphone },
        ]).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-jrd-orange text-white'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="space-y-6">
          {/* Monthly Plans */}
          <div>
            <h3 className="text-text-secondary text-sm font-mono mb-4">
              <span className="text-jrd-orange">//</span> MONTHLY UNLIMITED PLANS
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {WASH_PLANS.map((plan, idx) => {
                const Icon = plan.icon;
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`card relative overflow-hidden ${
                      plan.badge === 'Best Value' ? 'border-jrd-orange/50' : ''
                    }`}
                  >
                    {plan.badge && (
                      <div className={`absolute top-0 right-0 px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-bl ${
                        plan.badge === 'Best Value'
                          ? 'bg-jrd-orange text-white'
                          : 'bg-jrd-green text-white'
                      }`}>
                        {plan.badge}
                      </div>
                    )}
                    <div className="card-body">
                      <div className="flex items-center gap-2 mb-4">
                        <Icon className={`w-6 h-6 ${
                          plan.color === 'jrd-orange' ? 'text-jrd-orange' : plan.color === 'jrd-green' ? 'text-jrd-green' : 'text-text-primary'
                        }`} />
                        <h4 className="text-text-primary font-bold">{plan.name}</h4>
                      </div>

                      <div className="mb-4">
                        <span className="text-3xl font-bold text-text-primary">${plan.price}</span>
                        <span className="text-text-muted text-sm">/mo</span>
                        <div className="text-text-muted text-xs mt-1">
                          Single wash: ${plan.singlePrice}
                        </div>
                      </div>

                      <div className="space-y-2">
                        {plan.features.map((feature) => (
                          <div key={feature} className="flex items-center gap-2 text-sm">
                            <Check className="w-3 h-3 text-jrd-green flex-shrink-0" />
                            <span className="text-text-secondary">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Single Wash */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card"
          >
            <div className="card-header">// SINGLE WASH PRICING</div>
            <div className="card-body">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: 'Xpress Basic', price: 12 },
                  { name: 'Xpress Plus Wheels', price: 15 },
                  { name: 'Xpress Everything', price: 20 },
                  { name: 'Xpress Platinum', price: 25 },
                ].map((wash) => (
                  <div key={wash.name} className="p-3 bg-bg-input rounded border border-text-muted/20 text-center">
                    <p className="text-text-secondary text-sm mb-1">{wash.name}</p>
                    <p className="text-2xl font-bold text-text-primary">${wash.price}</p>
                  </div>
                ))}
              </div>
              <p className="text-text-muted text-xs mt-3 text-center">
                Add-ons: Lava Shine ($4) / Ice Tire Shine ($3) / Hot Wax ($2)
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div className="card-header flex items-center justify-between">
            <span>// ACTIVE MEMBERS ({SAMPLE_MEMBERS.length})</span>
            <span className="text-text-muted text-xs">156 total active</span>
          </div>
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-text-muted/20">
                    <th className="text-left text-text-muted text-xs font-mono p-3">MEMBER</th>
                    <th className="text-left text-text-muted text-xs font-mono p-3">PLAN</th>
                    <th className="text-left text-text-muted text-xs font-mono p-3">VEHICLE</th>
                    <th className="text-left text-text-muted text-xs font-mono p-3">RFID</th>
                    <th className="text-right text-text-muted text-xs font-mono p-3">WASHES</th>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE_MEMBERS.map((member) => (
                    <tr key={member.id} className="border-b border-text-muted/10 hover:bg-bg-card-hover transition-colors">
                      <td className="p-3">
                        <p className="text-text-primary text-sm">{member.name}</p>
                        <p className="text-text-muted text-xs">Since {new Date(member.since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</p>
                      </td>
                      <td className="p-3">
                        <span className={`text-sm font-medium ${
                          member.plan.includes('Platinum') ? 'text-jrd-orange' :
                          member.plan.includes('Everything') ? 'text-jrd-green' :
                          'text-text-secondary'
                        }`}>
                          {member.plan}
                        </span>
                      </td>
                      <td className="p-3">
                        <p className="text-text-secondary text-sm">{member.vehicle}</p>
                        <p className="text-text-muted text-xs font-mono">{member.plate}</p>
                      </td>
                      <td className="p-3">
                        <span className="text-text-muted text-xs font-mono bg-bg-input px-2 py-1 rounded">
                          {member.rfid}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-text-primary font-mono">{member.washCount}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Kiosk Tab */}
      {activeTab === 'kiosk' && (
        <div className="space-y-6">
          {/* XpresWash Connection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card"
          >
            <div className="card-header flex items-center justify-between">
              <span>// XPRESWASH KIOSK CONNECTION</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                <span className="text-yellow-500 text-xs">Standby</span>
              </div>
            </div>
            <div className="card-body">
              <div className="text-center py-8">
                <Bluetooth className="w-16 h-16 mx-auto text-text-muted/50 mb-4" />
                <h3 className="text-lg font-bold text-text-primary mb-2">Kiosk Not Connected</h3>
                <p className="text-text-secondary text-sm mb-6 max-w-md mx-auto">
                  Connect to your XpresWash kiosk via Bluetooth to activate wash lanes,
                  scan RFID tags, and process payments.
                </p>

                <div className="flex items-center justify-center gap-3">
                  <button className="btn-primary flex items-center gap-2">
                    <Bluetooth className="w-4 h-4" />
                    Scan for Kiosk
                  </button>
                  <button className="btn-ghost flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Manual Connect
                  </button>
                </div>

                <div className="mt-6 p-4 bg-bg-input rounded border border-text-muted/20 max-w-sm mx-auto text-left">
                  <p className="text-text-muted text-xs font-mono mb-2">// CONNECTION INFO</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-text-muted">Protocol</span>
                      <span className="text-text-secondary">Bluetooth LE</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">System</span>
                      <span className="text-text-secondary">XpresWash</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">RFID</span>
                      <span className="text-text-secondary">XpresRFID Active</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Location</span>
                      <span className="text-text-secondary">Maplewood, MN</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Wash Bay Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
          >
            <div className="card-header">// WASH BAY STATUS</div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { bay: 1, status: 'active', vehicle: '2023 Subaru Outback', member: 'Sarah Johnson', plan: 'Platinum', progress: 65, timeLeft: '2:15' },
                  { bay: 2, status: 'idle', vehicle: null, member: null, plan: null, progress: 0, timeLeft: null },
                ].map((bay) => (
                  <div key={bay.bay} className={`p-4 rounded-lg border ${
                    bay.status === 'active'
                      ? 'bg-jrd-green/5 border-jrd-green/30'
                      : 'bg-bg-input border-text-muted/20'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-text-primary font-bold">Bay {bay.bay}</span>
                      <span className={`text-xs font-mono px-2 py-1 rounded ${
                        bay.status === 'active'
                          ? 'bg-jrd-green/20 text-jrd-green'
                          : 'bg-text-muted/10 text-text-muted'
                      }`}>
                        {bay.status.toUpperCase()}
                      </span>
                    </div>

                    {bay.status === 'active' ? (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <Car className="w-4 h-4 text-jrd-green" />
                          <span className="text-text-secondary text-sm">{bay.vehicle}</span>
                        </div>
                        <p className="text-text-muted text-xs mb-3">
                          {bay.member} - {bay.plan}
                        </p>
                        <div className="h-2 bg-bg-darker rounded-full overflow-hidden mb-1">
                          <motion.div
                            className="h-full bg-gradient-to-r from-jrd-orange to-jrd-green"
                            initial={{ width: 0 }}
                            animate={{ width: `${bay.progress}%` }}
                            transition={{ duration: 1 }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs text-text-muted">
                          <span>{bay.progress}%</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {bay.timeLeft} remaining
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-text-muted text-sm">
                        Ready for next vehicle
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
